import { Injectable, inject, signal, computed } from '@angular/core';
import { CurrencyInfo } from '../models';
import { UserStateService } from './user-state.service';
import { ApiService } from './api.service';
import { firstValueFrom, of, catchError } from 'rxjs';

/**
 * Price Service - handles currency formatting
 * Converted from PriceUtil.as
 */
@Injectable({
  providedIn: 'root'
})
export class PriceService {
  private readonly userState = inject(UserStateService);
  private readonly api = inject(ApiService);

  /** Currency codes whose symbol is placed BEFORE the amount (e.g. £0,54,
   * $0.54). Everything else (EUR, AOA, RON, MZN, …) suffixes the symbol. */
  private static readonly SYMBOL_PREFIX_CODES = new Set(['USD', 'BRL', 'GBP', 'CAD']);

  /** localStorage key of the display-only price inflation factor. */
  private static readonly INFLATION_KEY = 'priceInflationFactor';
  static readonly INFLATION_MIN = 1;
  static readonly INFLATION_MAX = 5;

  /**
   * Display-only price multiplier (1.0–5.0, 0.1 steps) — the "Inflacionar
   * Preços" demo tool in the user menu. Applied EXCLUSIVELY inside
   * `formatWithCurrency` (the single formatting funnel every user-facing
   * price goes through), so raw values in pricing state, cart payloads,
   * draft saves and backend calls are never touched — an active factor can
   * never corrupt an order. Persisted in localStorage so a refresh keeps it.
   */
  readonly inflationFactor = signal<number>(PriceService.readStoredInflation());

  private static clampInflation(value: number): number {
    if (!isFinite(value) || value <= 0) return PriceService.INFLATION_MIN;
    const rounded = Math.round(value * 10) / 10;
    return Math.min(PriceService.INFLATION_MAX, Math.max(PriceService.INFLATION_MIN, rounded));
  }

  private static readStoredInflation(): number {
    try {
      return PriceService.clampInflation(Number(localStorage.getItem(PriceService.INFLATION_KEY)));
    } catch {
      return PriceService.INFLATION_MIN;
    }
  }

  setInflationFactor(factor: number): void {
    const clamped = PriceService.clampInflation(Number(factor));
    this.inflationFactor.set(clamped);
    try {
      if (clamped > PriceService.INFLATION_MIN) {
        localStorage.setItem(PriceService.INFLATION_KEY, String(clamped));
      } else {
        localStorage.removeItem(PriceService.INFLATION_KEY);
      }
    } catch {
      // Storage unavailable — the in-memory factor still applies this session.
    }
  }

  // Currencies list
  private currencies = signal<CurrencyInfo[]>([]);
  
  // Current currency (based on user's currencyId)
  currentCurrency = signal<CurrencyInfo | null>(null);
  
  // Display currency (based on user's displayCurrencyId, if different from current)
  displayCurrency = signal<CurrencyInfo | null>(null);

  // Default EUR currency
  private readonly defaultCurrency: CurrencyInfo = {
    id: 1,
    code: 'EUR',
    symbol: '€',
    fractionalDigits: 2,
    decimalSeparator: ',',
    groupingSeparator: '.',
    useGrouping: 1
  };

  // Default BRL currency. id 3 matches the backend's currencies table
  // (login response: currency { id: 3, code: 'BRL' }) — it was wrongly
  // 2 here, so the pre-load fallbacks below never matched BRL orders.
  private readonly brlCurrency: CurrencyInfo = {
    id: 3,
    code: 'BRL',
    symbol: 'R$',
    fractionalDigits: 2,
    decimalSeparator: ',',
    groupingSeparator: '.',
    useGrouping: 1
  };

  /**
   * Load currencies from API
   */
  async loadCurrencies(): Promise<void> {
    try {
      console.log('[PriceService] Loading currencies...');
      const response = await firstValueFrom(
        this.api.get<CurrencyInfo[]>('/common/currencies').pipe(
          catchError(err => {
            console.error('[PriceService] Error loading currencies:', err);
            return of([]);
          })
        )
      );

      if (Array.isArray(response) && response.length > 0) {
        const currencies = response.map(c => ({
          id: Number(c.id),
          code: c.code || '',
          symbol: c.symbol || '',
          fractionalDigits: Number(c.fractionalDigits ?? 2),
          decimalSeparator: c.decimalSeparator || ',',
          groupingSeparator: c.groupingSeparator || '.',
          useGrouping: Number(c.useGrouping ?? 1)
        }));
        
        this.currencies.set(currencies);
        console.log('[PriceService] Loaded currencies:', currencies.length, currencies.map(c => c.code));
        console.log('[PriceService] Currency IDs:', currencies.map(c => ({ id: c.id, code: c.code })));
        
        // Set default currency (EUR = 1) - will be overridden by initializeForUser
        this.setCurrency(1);
      } else {
        console.warn('[PriceService] No currencies returned, using defaults');
        this.currencies.set([this.defaultCurrency, this.brlCurrency]);
        this.setCurrency(1);
      }
    } catch (err) {
      console.error('[PriceService] Error loading currencies:', err);
      // Use default currencies
      this.currencies.set([this.defaultCurrency, this.brlCurrency]);
      this.setCurrency(1);
    }
  }

  /**
   * Set current currency by ID
   */
  setCurrency(currencyId: number): void {
    const currencies = this.currencies();
    if (!currencies || currencies.length === 0) {
      console.warn('[PriceService] No currencies loaded, using default');
      this.currentCurrency.set(this.defaultCurrency);
      return;
    }

    const currency = currencies.find(c => c.id === currencyId);
    if (currency) {
      this.currentCurrency.set(currency);
      console.log('[PriceService] Set currency to:', currency.code, currency.symbol);
    } else {
      console.warn('[PriceService] Currency not found:', currencyId, ', using default');
      this.currentCurrency.set(currencies[0] || this.defaultCurrency);
    }
  }

  /**
   * Select the user's authoritative currency object directly. The backend
   * sends the full currency (code/symbol/separators) on the user, and it can
   * describe a currency that's NOT in the global /common/currencies list
   * (e.g. USD) — in which case setCurrency(id) would fail the lookup and fall
   * back to EUR. Register it into the list (so later id lookups also resolve)
   * and set it current.
   */
  useCurrency(currency: CurrencyInfo): void {
    if (!currency || currency.id == null) return;
    const normalized: CurrencyInfo = {
      id: Number(currency.id),
      code: currency.code || '',
      symbol: currency.symbol || '',
      fractionalDigits: Number(currency.fractionalDigits ?? 2),
      decimalSeparator: currency.decimalSeparator || ',',
      groupingSeparator: currency.groupingSeparator || '.',
      useGrouping: Number(currency.useGrouping ?? 1)
    };
    const list = this.currencies();
    if (!list.some(c => c.id === normalized.id)) {
      this.currencies.set([...list, normalized]);
    }
    this.currentCurrency.set(normalized);
    console.log('[PriceService] Using user currency:', normalized.code, normalized.symbol);
  }

  /**
   * Set display currency by ID (for showing alternative currency)
   */
  setDisplayCurrency(currencyId: number): void {
    const current = this.currentCurrency();
    if (!current || current.id === currencyId) {
      this.displayCurrency.set(null);
      return;
    }

    const currencies = this.currencies();
    const currency = currencies.find(c => c.id === currencyId);
    if (currency) {
      this.displayCurrency.set(currency);
      console.log('[PriceService] Set display currency to:', currency.code);
    } else {
      this.displayCurrency.set(null);
    }
  }

  /**
   * Initialize currency based on logged-in user
   */
  initializeForUser(): void {
    const user = this.userState.user();
    console.log('[PriceService] initializeForUser - user:', user ? { id: user.id, currencyId: user.currencyId, displayCurrencyId: user.displayCurrencyId } : null);
    if (user) {
      console.log('[PriceService] Initializing for user, currencyId:', user.currencyId, 'displayCurrencyId:', user.displayCurrencyId);
      console.log('[PriceService] Available currencies:', this.currencies().map(c => ({ id: c.id, code: c.code })));
      // Prefer the authoritative currency object (handles currencies missing
      // from the global list, e.g. USD); fall back to id-lookup otherwise.
      if (user.currency) {
        this.useCurrency(user.currency);
      } else {
        this.setCurrency(user.currencyId || 1);
      }
      this.setDisplayCurrency(user.displayCurrencyId || user.currencyId || 1);
    } else {
      console.log('[PriceService] No user found, keeping default currency');
    }
  }

  /**
   * Format a price value using current currency
   */
  format(value: number | string | null | undefined): string {
    const numValue = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
    const safeValue = isNaN(numValue) ? 0 : numValue;
    const currency = this.currentCurrency() || this.defaultCurrency;
    return this.formatWithCurrency(safeValue, currency);
  }

  /**
   * Format a price value using a SPECIFIC currency id (e.g. the
   * currencyId of an order being viewed). Falls back through the
   * loaded currencies → current currency → default if the id can't
   * be matched. Use this when the viewer's session currency may
   * differ from the order's currency (e.g. staff viewing a BR order
   * from a PT session).
   */
  formatForCurrencyId(value: number | string | null | undefined, currencyId: number | null | undefined): string {
    if (currencyId == null) {
      return this.format(value);
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
    const safeValue = isNaN(numValue) ? 0 : numValue;

    const currencies = this.currencies();
    const match = currencies.find(c => c.id === currencyId);
    if (match) {
      return this.formatWithCurrency(safeValue, match);
    }
    // Hard-coded fallbacks for the two well-known ids covered by the
    // built-in defaults. Useful before the /common/currencies endpoint
    // responds — staff viewing a BR order still gets BRL formatting.
    if (currencyId === this.brlCurrency.id) {
      return this.formatWithCurrency(safeValue, this.brlCurrency);
    }
    if (currencyId === this.defaultCurrency.id) {
      return this.formatWithCurrency(safeValue, this.defaultCurrency);
    }
    // Unknown id — best-effort with whatever's current/default.
    return this.format(value);
  }

  /**
   * One-shot formatting with an EXPLICIT inflation factor (e.g. the quote
   * PDF's on-the-fly override) — the session factor stays untouched.
   */
  formatWithInflation(value: number | string | null | undefined, factor: number): string {
    const numValue = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
    const safeValue = isNaN(numValue) ? 0 : numValue;
    const currency = this.currentCurrency() || this.defaultCurrency;
    return this.formatWithCurrency(safeValue, currency, factor);
  }

  /**
   * Format a price with a specific currency
   */
  formatWithCurrency(value: number, currency: CurrencyInfo, inflationOverride?: number): string {
    // Display-only inflation (see inflationFactor). Applied here — the single
    // funnel of every on-screen price — so parts and totals scale coherently
    // (k·a + k·b = k·(a+b)) and nothing outside formatting is affected.
    // An explicit override (quote PDF) replaces the session factor for THIS
    // call only.
    value = value * (inflationOverride ?? this.inflationFactor());
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    
    // Format the number
    const fixed = absValue.toFixed(currency.fractionalDigits);
    const [intPart, decPart] = fixed.split('.');
    
    // Add grouping separators
    let formattedInt = intPart;
    if (currency.useGrouping === 1 && currency.groupingSeparator) {
      formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, currency.groupingSeparator);
    }
    
    // Combine with decimal separator
    const formattedNumber = decPart 
      ? `${formattedInt}${currency.decimalSeparator}${decPart}`
      : formattedInt;
    
    // Currency symbol placement. EUR and the suffix-style currencies (AOA Kz,
    // RON lei, MZN MT, …) put the symbol after the amount; the others (USD $,
    // BRL R$, GBP £, CAD $) put it before. The backend currency object has no
    // position flag, so this is decided by convention per code.
    if (PriceService.SYMBOL_PREFIX_CODES.has(currency.code)) {
      // No space for £/$ (£0.27, $0.27); BRL conventionally keeps one (R$ 0,27).
      const gap = currency.code === 'BRL' ? ' ' : '';
      return `${sign}${currency.symbol}${gap}${formattedNumber}`;
    } else {
      return `${sign}${formattedNumber} ${currency.symbol}`;
    }
  }

  /**
   * Get the currency symbol
   */
  getSymbol(): string {
    return this.currentCurrency()?.symbol || this.defaultCurrency.symbol;
  }

  /**
   * Get the currency code
   */
  getCode(): string {
    return this.currentCurrency()?.code || 'EUR';
  }

  /**
   * Resolve the ISO currency code (e.g. 'EUR', 'BRL') for a SPECIFIC
   * currency id — typically an order's currencyId, which may differ from
   * the viewer's session currency. Mirrors formatForCurrencyId's fallback
   * chain: loaded currencies → built-in BRL/EUR defaults → current/default.
   * Used to boot external SDKs (e.g. PayPal) in the order's own currency.
   */
  codeForCurrencyId(currencyId: number | null | undefined): string {
    if (currencyId == null) {
      return this.getCode();
    }
    const match = this.currencies().find(c => c.id === currencyId);
    if (match) {
      return match.code;
    }
    if (currencyId === this.brlCurrency.id) {
      return this.brlCurrency.code;
    }
    if (currencyId === this.defaultCurrency.id) {
      return this.defaultCurrency.code;
    }
    return this.getCode();
  }
}
